/**
 * The robot dog: a body that shows what has been fitted to it, and the loose legs that
 * wait on their tables to be fitted.
 *
 * Design D8 puts the whole drawing on the host's side: `RobotDogBody` reads the store's
 * attachments for its own id — a discrete read, a handful of times a run, never a
 * per-frame one — and draws a leg mesh at each filled socket from `ASSEMBLIES` and a stub
 * at each empty one. `RobotDogLeg` draws the same leg shape at full size on its table and
 * hides it the moment its id shows up in the attachments, the way `Screwdriver` hides in
 * a pocket. Because an attached leg is only ever drawn *inside* the body's `<RigidBody>`,
 * carrying, throwing and the interaction ray already treat the whole assembly as one body
 * — there is no second collider and no re-parenting to get that for free (design D8).
 *
 * Design D2 sizes the body's box for the whole standing dog, torso and leg space both, so
 * the torso is drawn at the top of the box and every socket sits below it at the height a
 * finished dog's legs actually reach the ground — nothing moves when the last leg goes on.
 *
 * The actual shapes — capsule torso, muzzled head, tail, jointed legs — live in
 * `dogGeometry.tsx` (`robot-dog-follows-player` design D8): this file stays physics and
 * store wiring, that one stays pure drawing shared by the attached and the loose leg.
 *
 * Once every socket is filled the store names this host as `followerId`, and `useDogFollow`
 * walks it after the player (design D5 of `robot-dog-follows-player`): this file's part of
 * that is one flag — am I the follower and am I not in the player's hands — handed to the
 * hook, whose limb refs come back to be threaded into the geometry. The whole dog draws
 * inside one rig group so the gait's bob and roll never move the collider.
 */

import { CuboidCollider, RigidBody, type RapierRigidBody } from '@react-three/rapier';
import { useEffect, useRef } from 'react';
import { BackSide } from 'three';
import { ASSEMBLIES, attachedTo, isPartLoose, type Socket } from '../game/assembly';
import { OBJECT_KINDS } from '../game/objectKinds';
import type { PlacedObject } from '../game/profile/buildGeometry';
import { useGameStore } from '../store/gameStore';
import { boxPosition, halfExtents } from './boxUnits';
import {
  DogContactShadow,
  DogHead,
  DogLeg,
  DogTail,
  DogTorso,
  JOINT_COLOR,
} from './dogGeometry';
import { useDogFollow, type LegJointRefs } from './useDogFollow';
import { getGradientMap } from './materials';
import { PartBeacon } from './PartBeacon';
import {
  CARRYABLE_GROUPS,
  getObjectBody,
  registerObjectBody,
  unregisterObjectBody,
} from './objectRegistry';
import type { BodyUserData } from './useInteractionTarget';

const ZERO = { x: 0, y: 0, z: 0 };

/** The canonical leg shape: the loose leg's own box, so an attached leg draws at the exact
 *  same size (design D8 — what you pick up is what appears on the dog). */
const LEG_SIZE = OBJECT_KINDS['robot-dog-leg'].size;

/** The dog body's own assembly spec — sockets and the part kind that fits them. */
const BODY_ASSEMBLY = ASSEMBLIES['robot-dog-body'];

type BodyProps = {
  object: PlacedObject;
};

export function RobotDogBody({ object }: BodyProps) {
  const bodyRef = useRef<RapierRigidBody>(null);
  const isFocused = useGameStore((store) => store.focus?.id === object.id);
  // A discrete read of the whole map, not a per-attachment subscription: it changes at
  // most a handful of times a run, on an `attachPart` call, never inside a frame loop.
  const attachments = useGameStore((store) => store.attachments);
  // The body is a carryable like any other part (design D1) — it raises its own beacon
  // while it sits loose in the world, which stops the whole assembly once carried or
  // once every socket is filled and it is picked up to leave with.
  const carriedObjectId = useGameStore((store) => store.carriedObjectId);
  const pocket = useGameStore((store) => store.pocket);
  const isLoose = isPartLoose(object.id, object.kind, { carriedObjectId, pocket, attachments });
  // Whether *this* host is the one walking (design D3/D5). `followerId` is set on the attach
  // that finishes an assembly and cleared by a restart, so the only other question is
  // whether it is in the player's hands — carrying suspends the walk, and setting it down
  // resumes it through this same flag, which is the whole of the lifecycle's trigger.
  const followerId = useGameStore((store) => store.followerId);
  const isFollowing = followerId === object.id && carriedObjectId !== object.id;
  const { rig: rigRef, head: headRef, tail: tailRef, legs: legRefs } =
    useDogFollow(bodyRef, isFollowing, object.box.size.y);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    registerObjectBody(object.id, body);
    return () => unregisterObjectBody(object.id);
  }, [object.id]);

  const { size } = object.box;
  const sockets = BODY_ASSEMBLY?.sockets ?? [];
  const filled = new Set(attachedTo(attachments, object.id).map((attachment) => attachment.socketId));

  return (
    <RigidBody
      ref={bodyRef}
      colliders={false}
      position={boxPosition(object.box)}
      userData={
        { kind: 'carryable', id: object.id, objectKind: object.kind } satisfies BodyUserData
      }
    >
      <CuboidCollider args={halfExtents(object.box)} collisionGroups={CARRYABLE_GROUPS} />

      {/* The rig group: the gait bobs and rolls it, and the collider above — fixed to the
          RigidBody itself — never moves with it. */}
      <group ref={rigRef}>
        <DogTorso size={size} isFocused={isFocused} castShadow={!isFollowing} />
        <DogHead size={size} headRef={headRef} castShadow={!isFollowing} />
        <DogTail size={size} tailRef={tailRef} castShadow={!isFollowing} />

        {sockets.map((socket) => (
          <SocketMesh
            key={socket.id}
            socket={socket}
            filled={filled.has(socket.id)}
            joints={legRefs[socket.id]}
            castShadow={!isFollowing}
          />
        ))}
      </group>

      {/* A walking dog casts a disc instead of a real shadow (design D7), and the disc sits
          outside the rig so the bob and roll above never tip it off the ground. */}
      {isFollowing && <DogContactShadow size={size} />}

      {isLoose && <PartBeacon />}
    </RigidBody>
  );
}

/**
 * One socket's worth of geometry: the shared `DogLeg` when filled, a short stub when it is
 * not. `isHind` reads off the socket id (`back-left`/`back-right`) rather than being
 * hardcoded, the same way `DOG_LEG_SOCKETS` itself is read rather than duplicated.
 */
function SocketMesh({
  socket,
  filled,
  joints,
  castShadow = true,
}: {
  socket: Socket;
  filled: boolean;
  /** This socket's hip and knee groups, from `useDogFollow`; absent for a socket no gait
   *  knows about, which is how an unfinished dog's legs stay still. */
  joints?: LegJointRefs;
  castShadow?: boolean;
}) {
  // Long enough to hang clear of the torso the socket now sits inside (`DOG_LEG_SOCKETS`
  // places the hip, not the foot), short enough that it still reads as a peg waiting for a
  // leg rather than as a leg: it stops well above the ground a fitted leg reaches.
  const stubHeight = LEG_SIZE.y * 0.9;
  const stubRadius = Math.min(LEG_SIZE.x, LEG_SIZE.z) * 0.5;
  return (
    <group
      name={`socket-${socket.id}`}
      position={[socket.offset.x, socket.offset.y, socket.offset.z]}
    >
      {filled ? (
        <group name="leg">
          <DogLeg
            size={LEG_SIZE}
            isHind={socket.id.startsWith('back')}
            hipRef={joints?.hip}
            kneeRef={joints?.knee}
            castShadow={castShadow}
          />
        </group>
      ) : (
        <mesh
          name="stub"
          position={[0, -stubHeight / 2, 0]}
          castShadow={castShadow}
          receiveShadow
        >
          <cylinderGeometry args={[stubRadius, stubRadius, stubHeight, 8]} />
          <meshToonMaterial color={JOINT_COLOR} gradientMap={getGradientMap()} />
        </mesh>
      )}
    </group>
  );
}

type LegProps = {
  object: PlacedObject;
};

/**
 * A loose leg, lying on a table until it is attached. Physically it is a carryable like
 * any other — same dynamic body, same registration, same `userData` — so it can be picked
 * up, pocketed, carried to the dog and attached exactly the way `availableAction` expects.
 *
 * Attaching parks and disables its body (design D7, `parkBody`) but leaves the component
 * mounted, so this is also the one place that knows how to put the leg back on its table:
 * when a restart empties the attachments, the transition from attached to not attached is
 * this component's own cue to re-enable its body and return it to where it was placed.
 * The pocket has `PocketController` do this for every kind at once because a pocket holds
 * anything stowable; an attachment is always a leg, and the leg already watches whether it
 * is attached, so it owns this pass itself rather than a controller learning its kind.
 */
export function RobotDogLeg({ object }: LegProps) {
  const bodyRef = useRef<RapierRigidBody>(null);
  const isFocused = useGameStore((store) => store.focus?.id === object.id);
  const isAttached = useGameStore((store) => store.attachments[object.id] !== undefined);
  // A leg is stowable, so it can leave the world by the pocket as well as by the dog.
  // `PocketController` parks its body where it was last held — in front of the eye — so
  // a mesh left drawn would hang there motionless. `Screwdriver` hides for the same
  // reason; a leg has two ways out of the world and has to answer for both.
  const isStowed = useGameStore((store) =>
    store.pocket.some((item) => item?.id === object.id),
  );
  // The beacon's own question (design D1/D2): not carried either, on top of the two the
  // mesh above already asks. A held leg is still drawn — the player is looking at it — so
  // this is a separate condition from `isAttached`/`isStowed`, not a replacement for them.
  const carriedObjectId = useGameStore((store) => store.carriedObjectId);
  const pocket = useGameStore((store) => store.pocket);
  const attachments = useGameStore((store) => store.attachments);
  const isLoose = isPartLoose(object.id, object.kind, { carriedObjectId, pocket, attachments });
  const wasAttachedRef = useRef(isAttached);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    registerObjectBody(object.id, body);
    return () => unregisterObjectBody(object.id);
  }, [object.id]);

  // A gap that predates the follow feature and narrows but does not close: this returns the
  // *legs* to their tables on a restart, and nothing returns the *body*. `restart` empties
  // the attachments, so the legs come home by the effect below and the dog stops walking —
  // `followerId` is cleared with them — but the torso is left standing wherever the player
  // last put it, now a bare dynamic prop again.
  //
  // What the torso was missing is now there: `objectHomes.ts` remembers every object's
  // placed pose, and `LostAndFound` returns the torso like anything else if it falls out of
  // the world. Restart is the part still open — it would be one call to `objectHome` here,
  // and it stays a deliberate change rather than a side effect of this one.
  useEffect(() => {
    // Only the falling edge matters: attached -> not attached is a restart (or any other
    // moment the store forgets the attachment), never the moment it is first fitted —
    // that leaves the body parked and disabled exactly as `attachPart` left it.
    if (wasAttachedRef.current && !isAttached) {
      const body = getObjectBody(object.id);
      if (body) {
        const position = boxPosition(object.box);
        body.setTranslation({ x: position[0], y: position[1], z: position[2] }, true);
        body.setRotation({ x: 0, y: 0, z: 0, w: 1 }, true);
        body.setLinvel(ZERO, true);
        body.setAngvel(ZERO, true);
        body.setEnabled(true);
      }
    }
    wasAttachedRef.current = isAttached;
  }, [isAttached, object.id, object.box]);

  const { size } = object.box;
  const radius = Math.min(size.x, size.z) / 2;

  return (
    <RigidBody
      ref={bodyRef}
      colliders={false}
      position={boxPosition(object.box)}
      ccd
      userData={
        { kind: 'carryable', id: object.id, objectKind: object.kind } satisfies BodyUserData
      }
    >
      <CuboidCollider args={halfExtents(object.box)} collisionGroups={CARRYABLE_GROUPS} />
      {/* Conditionally rendered rather than a `visible` toggle: an attached leg is drawn
          by the body instead (design D8), so there is nothing here worth keeping in the
          scene graph. A pocketed one is hidden by the same test — the HUD names the slot
          from the pocket itself, never from this mesh. */}
      {!isAttached && !isStowed && (
        <>
          {/* Drawn from the same `DogLeg` the attached socket uses, at its neutral pose
              (design D8): what is picked up here is what appears on the dog. The hip
              pivot sits at the top of the box, so the leg hangs through the same space
              the collider above already occupies. */}
          <group position={[0, size.y / 2, 0]}>
            <DogLeg size={size} isHind={false} />
          </group>
          {isFocused && (
            <mesh scale={[1.3, 1.1, 1.3]} castShadow={false} receiveShadow={false}>
              <cylinderGeometry args={[radius, radius, size.y, 8]} />
              <meshBasicMaterial color="#ffffff" side={BackSide} />
            </mesh>
          )}
        </>
      )}
      {isLoose && <PartBeacon />}
    </RigidBody>
  );
}
